import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from 'reactstrap';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import Button from 'components/App/Button';
import { CUSTOMER_ACCOUNT_TYPE } from 'constants/customerAccountType';
import INITIAL_VALUE from 'constants/initialValue';

import styles from './ModalEditCoinRefund.module.scss';

const REFUND_FIELDS = [
  {
    name: CUSTOMER_ACCOUNT_TYPE.MEMBER,
    label: 'Hoàn xu tài khoản Thành viên',
  },
  {
    name: CUSTOMER_ACCOUNT_TYPE.TOURIST,
    label: 'Hoàn xu tài khoản Du khách',
  },
  {
    name: CUSTOMER_ACCOUNT_TYPE.AGENT,
    label: 'Hoàn xu tài khoản Đại lý',
  },
  {
    name: CUSTOMER_ACCOUNT_TYPE.AMBASSADOR,
    label: 'Hoàn xu tài khoản Đại sứ',
  },
];

const percentSchema = Yup.mixed()
  .required('Phần trăm hoàn xu không được để trống')
  .test({
    message: 'Vui lòng nhập số lớn hơn 0',
    test(value) {
      if (value === '' || value === null || value === undefined) return false;
      const number = Number(value);
      return !Number.isNaN(number) && number > 0;
    },
  })
  .test({
    message: 'Phần trăm hoàn xu không được lớn hơn 100%',
    test(value) {
      if (value === '' || value === null || value === undefined) return false;
      return Number(value) <= 100;
    },
  });

function ModalEditCoinRefund(props) {
  const {
    isOpen,
    initialValues,
    isLoading,
    onToggleModal,
    onSubmit,
  } = props;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      [CUSTOMER_ACCOUNT_TYPE.MEMBER]: percentSchema,
      [CUSTOMER_ACCOUNT_TYPE.TOURIST]: percentSchema,
      [CUSTOMER_ACCOUNT_TYPE.AGENT]: percentSchema,
      [CUSTOMER_ACCOUNT_TYPE.AMBASSADOR]: percentSchema,
    }),
    onSubmit: (values) => {
      onSubmit({
        member: Number(values[CUSTOMER_ACCOUNT_TYPE.MEMBER]),
        tourist: Number(values[CUSTOMER_ACCOUNT_TYPE.TOURIST]),
        agent: Number(values[CUSTOMER_ACCOUNT_TYPE.AGENT]),
        ambassador: Number(values[CUSTOMER_ACCOUNT_TYPE.AMBASSADOR]),
      });
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    validation.resetForm({
      values: initialValues,
    });
  }, [isOpen, initialValues]);

  const CloseButton = useMemo(
    () => (
      <button
        className={styles.ButtonClose}
        onClick={onToggleModal}
        type="button"
      >
        <CloseButtonSvg />
      </button>
    ),
    [onToggleModal],
  );

  return (
    <Modal
      fade
      isOpen={isOpen}
      toggle={onToggleModal}
      className={styles.Modal}
    >
      <div className={styles.ModalHeaderWrapper}>
        <h2 className={styles.ModalHeaderTitle}>
          Hoàn xu theo loại tài khoản
        </h2>

        <ModalHeader
          toggle={onToggleModal}
          close={CloseButton}
          className={styles.ModalHeader}
        />
      </div>

      <form onSubmit={validation.handleSubmit}>
        <ModalBody className={styles.ModalBody}>
          <div className={styles.FormList}>
            {REFUND_FIELDS.map((field) => (
              <div key={field.name} className={styles.FormField}>
                <label className={styles.FormLabel} htmlFor={field.name}>
                  {field.label}
                </label>

                <AppInputNumberValidation
                  name={field.name}
                  id={field.name}
                  type="number"
                  placeholder="0"
                  validation={validation}
                  showCurrency="%"
                  value={validation.values[field.name]}
                  wrapperClassName={styles.InputWrapper}
                  inputClassName={styles.InputNumber}
                  thousandSeparator=""
                  decimalSeparator="."
                  formatOptions={{ decimalScale: 2 }}
                />
              </div>
            ))}
          </div>
        </ModalBody>

        <ModalFooter className={styles.ModalFooter}>
          <div className={styles.FooterButton}>
            <Button
              classes="Button Button__Cancel"
              onClick={onToggleModal}
              disabled={isLoading}
            >
              Huỷ
            </Button>

            <Button
              classes="Button Button__Save"
              type="submit"
              disabled={isLoading}
            >
              Lưu
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}

ModalEditCoinRefund.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialValues: PropTypes.instanceOf(Object),
  isLoading: PropTypes.bool,
  onToggleModal: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
};

ModalEditCoinRefund.defaultProps = {
  initialValues: INITIAL_VALUE.OBJECT,
  isLoading: false,
  onSubmit: INITIAL_VALUE.FUNCTION,
};

export default React.memo(ModalEditCoinRefund);
